import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { getCurrentUser, User } from '../services/authService';
import { adminService } from '../services/adminService';
import { startImpersonation } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import { APP_NAME } from '../constants';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const FiltersContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const UsersTable = styled.div`
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    background: transparent;
    box-shadow: none;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  
  @media (max-width: 768px) {
    overflow-x: visible;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr 150px;
  gap: 12px;
  padding: 16px 20px;
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  min-width: 850px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled.div<{ markedForDeletion?: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr 150px;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: ${props => props.markedForDeletion ? '#ffebee' : 'white'};
  align-items: center;
  min-width: 850px;
  
  &:hover {
    background-color: ${props => props.markedForDeletion ? '#ffcdd2' : '#f8f9fa'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #333;
`;

const UserEmail = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 2px;
`;

const StatusBadge = styled.span<{ status: 'verified' | 'unverified' | 'pending-deletion' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'verified':
        return 'background-color: #e8f5e8; color: #2e7d2e;';
      case 'unverified':
        return 'background-color: #fff3e0; color: #e65100;';
      case 'pending-deletion':
        return 'background-color: #ffebee; color: #c62828;';
      default:
        return 'background-color: #f5f5f5; color: #666;';
    }
  }}
`;

const RoleBadge = styled.span<{ role: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  
  ${props => {
    switch (props.role) {
      case 'admin':
        return 'background-color: #e3f2fd; color: #1976d2;';
      case 'manager':
        return 'background-color: #f3e5f5; color: #7b1fa2;';
      case 'staff':
        return 'background-color: #e8f5e8; color: #388e3c;';
      default:
        return 'background-color: #f5f5f5; color: #666;';
    }
  }}
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ActionButton = styled.button<{ variant?: 'admin' | 'remove' | 'delete' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;

  ${props => {
    switch (props.variant) {
      case 'admin':
        return `
          background: #1976d2; 
          color: white; 
          &:hover { background: #1565c0; }
        `;
      case 'remove':
        return `
          background: #ff9800; 
          color: white; 
          &:hover { background: #f57c00; }
        `;
      case 'delete':
        return `
          background: #f44336; 
          color: white; 
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: #2196f3; 
          color: white; 
          &:hover { background: #1976d2; }
        `;
    }
  }}

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileUserCard = styled.div<{ markedForDeletion?: boolean }>`
  display: none;
  background-color: ${props => props.markedForDeletion ? '#ffebee' : 'white'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileActionsContainer = styled.div`
  display: none;
  gap: 8px;
  margin-top: 12px;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const MobileUserHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MobileUserInfo = styled.div`
  flex: 1;
`;

const MobileUserName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 16px;
  margin-bottom: 4px;
`;

const MobileUserEmail = styled.div`
  font-size: 14px;
  color: #666;
`;

const MobileUserDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const MobileDetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileDetailLabel = styled.span`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 4px;
`;

const MobileDetailValue = styled.div`
  font-size: 14px;
  color: #333;
`;

const LoadingPlaceholder = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  background-color: #4caf50;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const AdminUsersPage: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setImpersonationStatus, refreshContext } = useAuth();

  const markedForDeletionFilter = searchParams.get('marked-for-deletion') === 'true';
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const userData = await getCurrentUser();
        if (userData) {
          setCurrentUser(userData);
          
          // Check if user is admin
          if (adminService.isAdmin(userData)) {
            let allUsers;
            if (markedForDeletionFilter) {
              console.log('Fetching users marked for deletion...');
              allUsers = await adminService.getUsersMarkedForDeletion();
              console.log('Users marked for deletion:', allUsers);
            } else {
              console.log('Fetching all users...');
              allUsers = await adminService.getAllUsers();
              console.log('All users:', allUsers);
            }
            setUsers(allUsers);
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setError(`Failed to load users: ${markedForDeletionFilter ? 'marked for deletion' : 'all users'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, markedForDeletionFilter]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToAdmin = () => {
    navigate('/admin');
  };

  const handleUpdateSystemRole = async (userId: string, systemRole: 'admin' | undefined) => {
    try {
      setError('');
      setSuccess('');
      await adminService.updateUserSystemRole(userId, systemRole || null);
      
      // Refresh users list
      const allUsers = markedForDeletionFilter 
        ? await adminService.getUsersMarkedForDeletion()
        : await adminService.getAllUsers();
      setUsers(allUsers);
      
      setSuccess(`User system role updated successfully`);
    } catch (error) {
      console.error('Error updating user system role:', error);
      setError('Failed to update user system role');
    }
  };

  const handleEditUser = (user: User) => {
    navigate(`/admin/users/${user.id}/edit`);
  };

  const getUserStatus = (user: User): 'verified' | 'unverified' | 'pending-deletion' => {
    if (user.markedForDeletion) return 'pending-deletion';
    if (user.emailVerified && user.phoneVerified) return 'verified';
    return 'unverified';
  };

  const handleSignInAs = async (user: User) => {
    try {
      setError('');
      const impersonatedUser = await startImpersonation(user.id);
      
      // Update the auth context using the refresh function
      refreshContext();
      
      // Navigate to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error starting impersonation:', error);
      setError('Failed to sign in as user. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingPlaceholder>Loading users...</LoadingPlaceholder>
      </Container>
    );
  }

  if (!currentUser || !adminService.isAdmin(currentUser)) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Admin Users</title>
        <meta name="description" content="Admin panel for managing users" />
      </Helmet>
      
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
          
          <PageTitle>
            {markedForDeletionFilter ? 'Users Marked for Deletion' : 'All Users'}
          </PageTitle>
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToAdmin}>Admin Dashboard</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>{markedForDeletionFilter ? 'Deletion Requests' : 'Users'}</span>
          </Breadcrumbs>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <FiltersContainer>
            <Button
              text="All Users"
              onClick={() => navigate('/admin/users')}
              outline={markedForDeletionFilter}
              bgColor="#381f74"
              textColor={markedForDeletionFilter ? '#381f74' : 'white'}
              width="auto"
              minWidth="120px"
            />
            <Button
              text="Deletion Requests"
              onClick={() => navigate('/admin/users?marked-for-deletion=true')}
              outline={!markedForDeletionFilter}
              bgColor="#f44336"
              textColor={markedForDeletionFilter ? 'white' : '#f44336'}
              width="auto"
              minWidth="160px"
            />
            <Button
              text="Deleted Users"
              onClick={() => navigate('/admin/users/deleted')}
              outline={true}
              bgColor="#666"
              textColor="#666"
              width="auto"
              minWidth="130px"
            />
          </FiltersContainer>
          <TableWrapper>
            <UsersTable>
              <TableHeader>
                <div>User</div>
                <div>Contact</div>
                <div>Status</div>
                <div>Role</div>
                <div>System Role</div>
                <div>Actions</div>
              </TableHeader>

              {users.map((user) => (
                <React.Fragment key={user.id}>
                  {/* Desktop Table Row */}
                  <TableRow markedForDeletion={user.markedForDeletion}>
                    <CellContent>
                      <UserName>{user.firstName} {user.lastName}</UserName>
                      <UserEmail>{user.email}</UserEmail>
                    </CellContent>

                    <CellContent>
                      <div>{user.phoneNumber || 'No phone'}</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        Joined: {user.createdAt?.toDate().toLocaleDateString() || 'Unknown'}
                      </div>
                    </CellContent>

                    <CellContent>
                      <StatusBadge status={getUserStatus(user)}>
                        {getUserStatus(user).replace('-', ' ')}
                      </StatusBadge>
                    </CellContent>

                    <CellContent>
                      <RoleBadge role={user.role || 'customer'}>
                        {user.role || 'customer'}
                      </RoleBadge>
                    </CellContent>

                    <CellContent>
                      {user.systemRole ? (
                        <RoleBadge role={user.systemRole}>
                          {user.systemRole}
                        </RoleBadge>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#666' }}>None</span>
                      )}
                    </CellContent>

                    <CellContent>
                      <ActionsContainer>
                        <ActionButton 
                          onClick={() => handleEditUser(user)}
                          title="Edit User"
                        >
                          <i className="bx bx-edit" style={{ fontSize: '16px' }}></i>
                        </ActionButton>

                        {!user.systemRole && (
                          <ActionButton 
                            variant="admin"
                            onClick={() => handleUpdateSystemRole(user.id, 'admin')}
                            title="Make Admin"
                          >
                            <i className="bx bx-user-plus" style={{ fontSize: '16px' }}></i>
                          </ActionButton>
                        )}
                        
                        {user.systemRole && (
                          <ActionButton 
                            variant="remove"
                            onClick={() => handleUpdateSystemRole(user.id, undefined)}
                            title="Remove Role"
                          >
                            <i className="bx bx-user-minus" style={{ fontSize: '16px' }}></i>
                          </ActionButton>
                        )}
                        
                        <ActionButton 
                          onClick={() => handleSignInAs(user)}
                          title="Sign in as this user"
                          style={{ background: '#9c27b0', color: 'white' }}
                        >
                          <i className="bx bx-log-in" style={{ fontSize: '16px' }}></i>
                        </ActionButton>
                      </ActionsContainer>
                    </CellContent>
                  </TableRow>

                  {/* Mobile Card */}
                  <MobileUserCard markedForDeletion={user.markedForDeletion}>
                    <MobileUserHeader>
                      <MobileUserInfo>
                        <MobileUserName>{user.firstName} {user.lastName}</MobileUserName>
                        <MobileUserEmail>{user.email}</MobileUserEmail>
                      </MobileUserInfo>
                      <StatusBadge status={getUserStatus(user)}>
                        {getUserStatus(user).replace('-', ' ')}
                      </StatusBadge>
                    </MobileUserHeader>

                    <MobileUserDetails>
                      <MobileDetailItem>
                        <MobileDetailLabel>Phone</MobileDetailLabel>
                        <MobileDetailValue>{user.phoneNumber || 'No phone'}</MobileDetailValue>
                      </MobileDetailItem>
                      
                      <MobileDetailItem>
                        <MobileDetailLabel>Joined</MobileDetailLabel>
                        <MobileDetailValue>
                          {user.createdAt?.toDate().toLocaleDateString() || 'Unknown'}
                        </MobileDetailValue>
                      </MobileDetailItem>
                      
                      <MobileDetailItem>
                        <MobileDetailLabel>Role</MobileDetailLabel>
                        <MobileDetailValue>
                          <RoleBadge role={user.role || 'customer'}>
                            {user.role || 'customer'}
                          </RoleBadge>
                        </MobileDetailValue>
                      </MobileDetailItem>
                      
                      <MobileDetailItem>
                        <MobileDetailLabel>System Role</MobileDetailLabel>
                        <MobileDetailValue>
                          {user.systemRole ? (
                            <RoleBadge role={user.systemRole}>
                              {user.systemRole}
                            </RoleBadge>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#666' }}>None</span>
                          )}
                        </MobileDetailValue>
                      </MobileDetailItem>
                    </MobileUserDetails>                    <MobileActionsContainer>
                      <Button
                        text="Edit"
                        onClick={() => handleEditUser(user)}
                        bgColor="#2196f3"
                        textColor="white"
                        width="auto"
                        minWidth="70px"
                      />

                      {!user.systemRole && (
                        <Button
                          text="Make Admin"
                          onClick={() => handleUpdateSystemRole(user.id, 'admin')}
                          bgColor="#1976d2"
                          textColor="white"
                          width="auto"
                          minWidth="90px"
                        />
                      )}
                      
                      {user.systemRole && (
                        <Button
                          text="Remove Role"
                          onClick={() => handleUpdateSystemRole(user.id, undefined)}
                          outline={true}
                          bgColor="#ff9800"
                          textColor="#ff9800"
                          width="auto"
                          minWidth="90px"
                        />
                      )}
                      
                      <Button
                        text="Sign in as"
                        onClick={() => handleSignInAs(user)}
                        bgColor="#9c27b0"
                        textColor="white"
                        width="auto"
                        minWidth="90px"
                      />
                    </MobileActionsContainer>
                  </MobileUserCard>
                </React.Fragment>
              ))}

              {users.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {markedForDeletionFilter ? 'No users marked for deletion' : 'No users found'}
                </div>
              )}
            </UsersTable>
          </TableWrapper>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default AdminUsersPage;
